import { Logger } from '~~/helpers/logger';
import type { CiaDependencies } from '~/composables/useCia/types';
import type { NEWSLETTER_ACTION_WITH_VOUCHER, NEWSLETTER_ACTION_WITHOUT_VOUCHER } from '~~/constants/cia';

export type NewsletterActionType =
  | typeof NEWSLETTER_ACTION_WITH_VOUCHER
  | typeof NEWSLETTER_ACTION_WITHOUT_VOUCHER
  | null;

type NewsletterDoiParams = {
  email: string;
  action: NewsletterActionType;
  userId?: string;
  emailMasked?: string;
  voucherDescription?: string;
  voucherValue?: number;
  minimumPurchaseValue?: number;
  voucherExpireTime?: number;
  status?: string;
  source?: string;
  shop?: string;
};

type NewsletterDoiOptionalFields = {
  emailMasked?: string;
  voucherDescription?: string;
  voucherValue?: number;
  minimumPurchaseValue?: number;
  voucherExpireTime?: number;
  status?: string;
  source?: string;
  shop?: string;
};

type NewsletterDoiRequest = {
  locale: string;
  email: string;
  newsletterAction?: NewsletterActionType;
  cookie?: string;
  user_id?: string;
} & NewsletterDoiOptionalFields;

export default (dependencies: CiaDependencies) => {

  const { fetch, languageAndCountry } = dependencies;

  return async ({
    email,
    action = null,
    userId,
    emailMasked,
    voucherDescription,
    voucherValue,
    minimumPurchaseValue,
    voucherExpireTime,
    status,
    source,
    shop,
  }: NewsletterDoiParams): Promise<unknown> => {

    if (import.meta.server) {

      if (import.meta.dev) {

        Logger.debug('cia|newsletterDOI SSR suppressed');
      
      }

      return Promise.resolve();
    
    }

    if (!email) {

      return Promise.resolve();
    
    }

    const optionalFields = {
      user_id: userId,
      emailMasked,
      voucherDescription,
      voucherValue,
      minimumPurchaseValue,
      voucherExpireTime,
      status,
      source,
      shop,
    };

    const payload: NewsletterDoiRequest = {
      locale: languageAndCountry,
      email,
      newsletterAction: action,
      ...Object.fromEntries(
        Object.entries(optionalFields).filter(([_, v]) => v !== undefined && v !== null),
      ),
    };

    return fetch('/cia/newsletter-doi', {
      method: 'POST',
      body: payload,
    });
  
  };

};
